import { Component, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';

import { EChartsOption } from 'echarts';
import { NgxEchartsDirective, provideEcharts } from 'ngx-echarts';

import { RegisterComponent } from '../../components/component-lookup';
import { VisualBaseComponent } from '../visual-base.component';
import { DateRange, Peer, StateService } from '../../services/state.service';
import { CorrelationMetric, InstitutionStatistical } from '../../services/metric.service';
import { CorrelationRate, RateService } from '../../services/rate.service';
import { forkJoin } from 'rxjs';
import { GlobalConstants } from '../../../shared/global-constants';
import { FilterCorrelationMetricPipe } from '../../pipes/filter-correlation-metric.pipe';
import { FilterCorrelationRatePipe } from '../../pipes/filter-correlation-rate.pipe';
import { GroupByPipe } from '../../../shared/pipes/group-by.pipe';
import { OrderByPipe } from '../../../shared/pipes/order-by';
import { VisualService } from '../../services/visual.service';
import { SessionService } from '../../../shared/services/session.service';

@RegisterComponent('CorrelationComponent')
@Component({
  selector: 'app-correlation',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, NgxEchartsDirective, FilterCorrelationMetricPipe,
    FilterCorrelationRatePipe, GroupByPipe, OrderByPipe],
  templateUrl: './correlation.component.html',
  styleUrl: './correlation.component.scss',
  providers: [provideEcharts()]
})
export class CorrelationComponent extends VisualBaseComponent {
  readonly institution = inject(StateService).institution;
  readonly peer = inject(StateService).peer;
  readonly peerName = inject(StateService).peerName;
  readonly filteredGroup = inject(StateService).filteredGroup;
  readonly dateRange = inject(StateService).dateRange;

  private sessionService = inject(SessionService);
  private rateService = inject(RateService);

  override initOptions = { height: 400 }
  override baseOptions: EChartsOption = {
    grid: {
      left: 10,
      right: 25,
      top: 20,
      bottom: 40,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      show: true,
      axisLabel: {
        color: '#243b5b'
      }
    },
    yAxis: {
      type: 'value',
      show: true,
      axisLabel: {
        color: '#243b5b'
      }
    },
    axisLabel: {
      color: '#002f8e'
    },
    series: [
      {
        type: 'bar'
      },
      {
        type: 'line',
        symbol: 'none'
      },
      {
        type: 'line',
        symbol: 'none'
      }
    ]
  };
  override tooltipOptions: EChartsOption = {
    tooltip: {
      trigger: 'axis',
      valueFormatter: value => VisualService.getValueDisplay(Number(value), this.metric()?.displayType)
    }
  };
  override printOptions: EChartsOption = {
    animation: false,
    series: [
      {
        type: 'bar'
      },
      {
        type: 'line',
        symbol: 'none',
        symbolSize: 7,
        lineStyle: {
          type: [20, 5],
          dashOffset: 5,
        }
      },
      {
        type: 'line',
        symbol: 'none'
      }
    ]
  };

  readonly correlationMetrics = signal<CorrelationMetric[]>([]);
  readonly metric = signal<CorrelationMetric | undefined>(undefined);
  readonly correlationRates = computed(() => this.metric()?.rates ?? []);
  readonly rate = signal<CorrelationRate | undefined>(undefined);

  private statisticals?: InstitutionStatistical[];
  readonly statistical = signal<InstitutionStatistical | undefined>(undefined);
  readonly bestStatistical = signal<InstitutionStatistical | undefined>(undefined);
  readonly insufficientData = computed(() => this.statistical()?.beta == null);

  private peerStatisticals?: InstitutionStatistical[];
  public peerStatistical = signal<InstitutionStatistical | undefined>(undefined);
  public peerBestStatistical = signal<InstitutionStatistical | undefined>(undefined);

  metricListOpen = false;
  rateListOpen = false;
  openMetricCategories: string[] = [];
  openRateCategories: string[] = [];
  metricSearch = '';
  rateSearch = '';
  noData = false;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  override resizedObserver = new ResizeObserver((entries) => {
    if (!this.echartsInstance?.isDisposed()) {
      this.echartsInstance?.resize();
    }
  });

  constructor() {
    super();

    effect(() => {
      const institution = this.institution();
      if (institution?.institutionType) {
        this.metricService.getCorrelationMetrics(institution.institutionType).subscribe(metrics => {
          setTimeout(() => {
            this.correlationMetrics.set(metrics);
            const metricId = this.sessionService
              .get(`correlation-metricId-${this.institution()?.institutionType}`)
              ?.toString() ?? this.metric()?.metricId ?? metrics[0]?.metricId;
            this.selectMetric(metricId);
          });
        });
      }
    });

    effect(() => {
      const peer = this.peer();
      const dateRange = this.dateRange();
      const metric = this.metric();
      const group = this.filteredGroup();
      const rate = this.rate();
      if (this.chartInit() && peer && dateRange && metric && group && rate) {
        this.updateVisual(peer, dateRange, metric, rate);
      }
    });
  }

  public updateVisual(peer: Peer, dateRange: DateRange, metric: CorrelationMetric, rate: CorrelationRate) {
    this.metricService.getInstitutionStatisticals(this.institution().iid, metric.metricId, dateRange).subscribe(statisticals => {
      this.statisticals = statisticals;
      setTimeout(() => {
        this.statistical.set(this.statisticals?.find(s => s.rateId == rate.rateId) ?? {} as InstitutionStatistical);
        this.bestStatistical.set(this.statisticals?.reduce((p, c) => ((p.rSquared ?? 0) > (c.rSquared ?? 0)) ? p : c, {} as InstitutionStatistical) ?? {} as InstitutionStatistical);
      });
    });

    if (peer.institution) {
      this.metricService.getInstitutionStatisticals(peer.institution.iid, metric.metricId, dateRange).subscribe(statisticals => {
        this.peerStatisticals = statisticals;
        setTimeout(() => {
          this.peerStatistical.set(this.peerStatisticals?.find(s => s.rateId == rate.rateId) ?? {} as InstitutionStatistical);
          this.peerBestStatistical.set(this.peerStatisticals?.reduce((p, c) => ((p.rSquared ?? 0) > (c.rSquared ?? 0)) ? p : c) ?? {} as InstitutionStatistical);

          if (!this.echartsInstance?.isDisposed()) {
            this.echartsInstance?.resize();
          }
        });
      });
    } else {
      setTimeout(() => {
        this.peerStatisticals = undefined;
        this.peerStatistical.set(undefined);
        this.peerBestStatistical.set(undefined);
      });
    }

    forkJoin({
      metricData: this.metricService.getMetricData(this.institution().iid, metric.metricId, peer),
      rateData: this.rateService.getRateData(rate.rateId)
    }).subscribe(({ metricData, rateData }) => {
      const metricDataSeries = this.visualService.getMetricSeriesPeer(this.metric()?.displayType, metricData, dateRange);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rateSeries: any[] = [];
      rateData.forEach((data: { reportDate: Date; value: number; }) => {
        const dataRow = metricDataSeries.dates.findIndex(d => { return new Date(d).getTime() == new Date(data.reportDate).getTime() });
        if (dataRow > -1) {
          rateSeries[dataRow] = Number(data.value).toFixed(2);
        }
      });

      if (metricDataSeries.institutionSeries.filter(v => v != null).length == 0) {
        this.echartsInstance?.clear();
        this.chartOptions = this.noDataOptions;
        this.noData = true;
      }
      else {
        if (this.noData) {
          this.setOptions();
          this.noData = false;
        }
        this.updateOptions = {
          xAxis: {
            data: metricDataSeries.dateSeries
          },
          series: [
            {
              name: rate.rateName,
              data: rateSeries,
              color: GlobalConstants.rateColor
            },
            {
              name: this.peerName(),
              data: metricDataSeries.peerSeries,
              color: this.colors[1],
            },
            {
              name: this.institution().name,
              data: metricDataSeries.institutionSeries,
              color: this.colors[0],
            }
          ]
        };
      }
      this.echartsInstance?.hideLoading();
    });
  }

  clearMetricSearch() {
    this.metricSearch = '';
    this.openMetricCategories = [];
  }

  clearRateSearch() {
    this.rateSearch = '';
    this.openRateCategories = [];
  }

  isMetricCategoryOpen(category: string) {
    return this.openMetricCategories.find(c => c == category);
  }

  isRateCategoryOpen(category: string) {
    return this.openRateCategories.find(c => c == category);
  }

  toggleMetricCategory(category: string) {
    if (this.openMetricCategories.find(c => c == category)) {
      this.openMetricCategories = this.openMetricCategories.filter(c => c != category);
    }
    else {
      this.openMetricCategories.push(category);
    }
  }

  toggleRateCategory(category: string) {
    if (this.openRateCategories.find(c => c == category)) {
      this.openRateCategories = this.openRateCategories.filter(c => c != category);
    }
    else {
      this.openRateCategories.push(category);
    }
  }

  selectMetric(metricId: string): void {
    const metric = this.correlationMetrics().find(m => m.metricId == metricId);
    if (metric) {
      this.sessionService.setAndWait(`correlation-metricId-${this.institution()?.institutionType}`, metricId).subscribe(() => {
        this.metric.set(metric);
        this.selectRate(undefined);
      });
      this.metricListOpen = false;
    }
  }

  selectRate(rateId: string | undefined): void {
    let rate: CorrelationRate | undefined = undefined;
    const key = `correlation-rateId-${this.metric()?.metricId}${this.institution()?.institutionType.toString()}`;

    if (rateId) {
      rate = this.correlationRates().find(r => r.rateId == rateId);
    }

    if (rate == undefined) {
      rateId = (this.sessionService.get(key) as CorrelationRate)?.rateId;
      rate = this.correlationRates().find(r => r.rateId == rateId);
    }

    if (rate == undefined) {
      rate = this.correlationRates().find(r => r.correlationsDefault == true);
    }

    if (rate == undefined) {
      rate = this.correlationRates()[0];
    }

    if (rate) {
      this.rate.set(rate);
      this.sessionService.set(key, rate, true);
      this.rateListOpen = false;
    }
  }
}
